import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  FormHelperText,
  Divider,
} from '@chakra-ui/react';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

export interface BookingPoolSettings {
  poolTimeOffsetHours: number;
  poolRadiusMiles: number;
  maxPoolMembers: number;
  poolInviteExpiryHours: number;
  gracePeriodMinutes: number;
  discountConfig: {
    baseDiscount: number;
    incrementalDiscount: number;
    maxDiscount: number;
    maxPoolSize: number;
  };
}

export interface AppSettings {
  id: string;
  paymentStrategy?: any;
  upfrontPercentage?: number;
  businessHours?: any;
  bookingConstraints?: any;
  serviceAreaSettings?: any;
  bookingPoolSettings?: BookingPoolSettings;
  holidays?: any[];
  technicianSchedules?: any;
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
}

const DEFAULT_POOL_SETTINGS: BookingPoolSettings = {
  poolTimeOffsetHours: 2,
  poolRadiusMiles: 5,
  maxPoolMembers: 10,
  poolInviteExpiryHours: 24,
  gracePeriodMinutes: 30,
  discountConfig: {
    baseDiscount: 5,
    incrementalDiscount: 2.5,
    maxDiscount: 25,
    maxPoolSize: 10,
  },
};

const BookingPoolSettingsComponent: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<BookingPoolSettings>(DEFAULT_POOL_SETTINGS);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data() as AppSettings;
        if (data.bookingPoolSettings) {
          // --- THIS IS THE FIX ---
          // Deep merge the loaded settings with the defaults
          // This ensures that if Firestore has an incomplete object,
          // the missing fields are filled in from DEFAULT_POOL_SETTINGS.
          const loadedSettings = data.bookingPoolSettings;
          const mergedSettings = {
            ...DEFAULT_POOL_SETTINGS, // Start with all default values
            ...loadedSettings,        // Override with any values from Firestore
            discountConfig: {
              ...DEFAULT_POOL_SETTINGS.discountConfig, // Start with default discount config
              ...(loadedSettings.discountConfig || {}), // Override with Firestore discount config (if it exists)
            },
          };
          setSettings(mergedSettings);
          // --- END OF FIX ---
        }
        // If !data.bookingPoolSettings, state correctly remains DEFAULT_POOL_SETTINGS
      }
      // If !settingsSnap.exists(), state correctly remains DEFAULT_POOL_SETTINGS
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load booking pool settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof BookingPoolSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDiscountConfigChange = (field: keyof BookingPoolSettings['discountConfig'], value: number) => {
    setSettings(prev => ({
      ...prev,
      discountConfig: {
        ...prev.discountConfig,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          bookingPoolSettings: settings,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          id: 'default',
          paymentStrategy: 'collect_full_at_booking',
          bookingPoolSettings: settings,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      toast({
        title: 'Success',
        description: 'Booking pool settings updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save booking pool settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_POOL_SETTINGS);
    toast({
      title: 'Reset',
      description: 'Booking pool settings reset to defaults',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          mb={4}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
        >
          Booking Pool Settings
        </Heading>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Configure booking pools to allow customers to book together at a discount.
              Pool bookings help maximize efficiency and offer savings to customers.
            </Text>
          </Box>
        </Alert>
      </Box>

      <Card mb={6}>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl>
                <FormLabel>Pool Time Offset (Hours)</FormLabel>
                <NumberInput
                  value={settings.poolTimeOffsetHours}
                  onChange={(_, val) => handleChange('poolTimeOffsetHours', val)}
                  min={1}
                  max={12}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Hours before/after booking time for pool start/end window
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Pool Radius (Miles)</FormLabel>
                <NumberInput
                  value={settings.poolRadiusMiles}
                  onChange={(_, val) => handleChange('poolRadiusMiles', val)}
                  min={1}
                  max={50}
                  precision={1}
                  step={0.5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Service area radius from pool address for matching bookings
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Max Pool Members</FormLabel>
                <NumberInput
                  value={settings.maxPoolMembers}
                  onChange={(_, val) => handleChange('maxPoolMembers', val)}
                  min={2}
                  max={20}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Maximum number of customers allowed in a single pool
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Pool Invite Expiry (Hours)</FormLabel>
                <NumberInput
                  value={settings.poolInviteExpiryHours}
                  onChange={(_, val) => handleChange('poolInviteExpiryHours', val)}
                  min={1}
                  max={168}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Hours until pool invitation expires
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Grace Period (Minutes)</FormLabel>
                <NumberInput
                  value={settings.gracePeriodMinutes}
                  onChange={(_, val) => handleChange('gracePeriodMinutes', val)}
                  min={0}
                  max={120}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Buffer time after end time before pool deactivation
                </FormHelperText>
              </FormControl>
            </SimpleGrid>

            <Divider />

            <Heading size="md">Discount Configuration</Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl>
                <FormLabel>Base Discount (%)</FormLabel>
                <NumberInput
                  value={settings.discountConfig.baseDiscount}
                  onChange={(_, val) => handleDiscountConfigChange('baseDiscount', val)}
                  min={0}
                  max={50}
                  precision={1}
                  step={0.5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Starting discount percentage for pools
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Incremental Discount (%)</FormLabel>
                <NumberInput
                  value={settings.discountConfig.incrementalDiscount}
                  onChange={(_, val) => handleDiscountConfigChange('incrementalDiscount', val)}
                  min={0}
                  max={25}
                  precision={1}
                  step={0.5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Additional discount per new member
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Max Discount (%)</FormLabel>
                <NumberInput
                  value={settings.discountConfig.maxDiscount}
                  onChange={(_, val) => handleDiscountConfigChange('maxDiscount', val)}
                  min={0}
                  max={100}
                  precision={1}
                  step={0.5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Maximum discount percentage achievable
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Max Pool Size</FormLabel>
                <NumberInput
                  value={settings.discountConfig.maxPoolSize}
                  onChange={(_, val) => handleDiscountConfigChange('maxPoolSize', val)}
                  min={2}
                  max={20}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Pool size at which max discount applies
                </FormHelperText>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      <Alert status="warning" mb={6}>
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Discount Example
          </Text>
          <Text fontSize="sm">
            With base discount 5% and incremental 2.5%: Pool of 1 = 5%, Pool of 2 = 7.5%, 
            Pool of 3 = 10%, Pool of 4 = 12.5% (capped at max discount)
          </Text>
        </Box>
      </Alert>

      <HStack spacing={3}>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving"
        >
          Save Pool Settings
        </Button>
        <Button
          variant="outline"
          onClick={handleReset}
        >
          Reset to Defaults
        </Button>
      </HStack>
    </Box>
  );
};

export default BookingPoolSettingsComponent;

